<script setup lang="ts" generic="TOption extends string | number">
import { nextTick, onMounted, onUnmounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useContext } from './context'

import Button from './Button.vue'
import Label from './Label.vue'
import { type WithAttributes, type Labeled, toHtml, fw } from './attributes'

/** Select a value from a list of labeled options. */

// TODO: Add machine-readable semantics with `type` and `autocomplete` attributes (https://www.w3.org/WAI/WCAG21/Understanding/identify-input-purpose)
// TODO: Default `uiBlockSize` here once typescript has fixed the bug preventing it from
// inferring Omit<UnresolvedGenericType, 'uiBlockSize'>
// - https://github.com/microsoft/TypeScript/issues/62572
// - https://github.com/microsoft/TypeScript/issues/28884
const props = defineProps<WithAttributes<{
  class: {
    shape?: 'shape-field-grow' | 'shape-field-minimal' | 'shape-field-full'
  }
  options: Map<TOption, string> | (Record<TOption, string> & object) | (readonly [TOption, string])[]
  placeholder?: string
  initial?: TOption
  icon?: string
  label?: string
  autofocus?: boolean
  uiBlockSize?: '24' | '40' | '48'
  required?: boolean
  disabled?: boolean

  /**
   * Indicates whether an element is being updated in the background and will not respond to user interactions.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-busy
   */
  ariaBusy?: 'true' | 'false' | boolean
} & Labeled>>()

const current = defineModel<TOption | string>()
const uiBlockSize = useContext().value.uiBlockSize ?? '48'

const { t } = useI18n()

const select = ref()

const previouslyFocusedElement = ref()

onMounted(() => props.autofocus && nextTick(() => {
  previouslyFocusedElement.value = document.activeElement
  previouslyFocusedElement.value?.blur()
  select.value.focus()
}))

current.value = current.value ?? props.initial

onUnmounted(() =>
  previouslyFocusedElement.value?.focus()
)

const selected = computed<TOption | string>({
  get(): TOption | string {
    return current.value ?? props.initial ?? props.placeholder ?? ''
  },
  set(newValue: TOption | string) {
    current.value = newValue
  }
})

const isMap = <K, V>(val: unknown): val is Map<K, V> => val instanceof Map

const iterableOptions = computed<(readonly [TOption, string])[]>(() =>
  Array.isArray(props.options)
    ? props.options
    : isMap<TOption, string>(props.options)
      ? Array.from(props.options.entries())
      // Todo: Once Object.entries is typed, remove assertion
      : Object.entries(props.options) as unknown as [TOption, string][])

const reset = () => {
  current.value = props.initial ?? ''
}
</script>

<template>
  <Label
    :label
    :required
  >
    <template
      v-if="$slots.label"
      #label
    >
      <slot name="label" />
    </template>

    <!--
      Single wrapping element: without it, this slot renders `<select>` and the
      caret `<div>` (and, conditionally, more) as separate sibling DOM nodes. That's
      harmless in a flex row, but inside a CSS Grid row (e.g. Table.vue) each sibling
      consumes its own grid cell, shifting every column after it. This wrapper makes
      Select always contribute exactly one grid item, however many children it has.
    -->
    <div :class="$style.selectContainer">
      <!-- TODO: Add a11y props for better discoverability of accessibility-enhancing attributes instead of lumping them under $attrs -->
      <select
        v-bind="{ ...$attrs, ...toHtml<'select'>(props) }"
        ref="select"
        v-model="selected"
        :class="[
          props.class,
          !props.class?.includes('shape-') && fw.shape.field,
          fw.color.defaultSolid
        ]"
        :ui-block-size
        :autofocus="autofocus || undefined"
        style="
          min-width: 48px;
          padding-right: 48px;
          border: none;
          outline-width: 4px;
          outline-offset: -4px;
          appearance: none;
          -webkit-appearance: none;
          background-image: none;
        "
      >
        <option
          v-for="([option, label_]) in placeholder ? [[placeholder, placeholder], ...iterableOptions] : iterableOptions"
          :key="String(option)"
          :value="option"
          :disabled="label_ === placeholder"
          :selected="label_ === placeholder"
        >
          {{ label_ }}
        </option>
      </select>

      <!-- Caret icon -->

      <div
        :class="$style.caret"
        style="position: absolute; inset: auto 16px calc(50% - 10px) auto; pointer-events: none;"
      >
        <Icon icon="bi:chevron-down" style="width: 12px; height: 12px; margin: auto;" />
      </div>

      <!-- Left side icon -->

      <div
        v-if="icon"
        :class="$style.prefix"
      >
        <Icon :icon="icon" />
      </div>

      <!-- Right side -->

      <div
        v-if="$slots['input-right']"
        :class="$style['input-right']"
      >
        <span :class="$style['span-right']">
          <slot name="input-right" />
        </span>
      </div>

      <!-- Reset -->

      <Button
        v-if="initial && initial !== current"
        class="color-default-ghost shape-circle"
        ui-block-size="24"
        :title="t('components.library.EditForm.button.reset')"
        :aria-label="t('components.library.EditForm.button.reset')"
        @click="reset"
      >
        <Icon icon="bi:arrow-counterclockwise" />
      </Button>
    </div>
  </Label>
</template>

<style module>
/*
 * Shrink-wraps to its content (the select) instead of stretching to fill its parent's
 * width, which is the default for a plain block box — and `justify-self: start`
 * counteracts the fact that CSS Grid items default to `justify-self: stretch`
 * regardless of their own display value (relevant when this sits directly in a
 * Table.vue row).
 *
 * Also the positioning root for the absolutely-positioned caret: `Label.vue` only
 * wraps its slot in a `position: relative` element when a `label` prop/slot is
 * given — without a label it renders the slot directly, so the caret would escape
 * to whatever positioned ancestor happens to exist further up the tree instead.
 */
.selectContainer {
  display: inline-block;
  justify-self: start;
  position: relative;
}
</style>
